/**
 * Projects API Service
 * 
 * Manages user projects via orbit-gateway proxy.
 * Projects are stored in Firestore for persistence across devices
 * and synced with the K3 World-State Ledger.
 */
import { getIdToken } from './firebase';

// Use orbit-gateway as proxy to internal services
const ORBIT_GATEWAY_URL = import.meta.env.VITE_ORBIT_GATEWAY_URL || 'https://orbit-gateway-buzbhnk2ha-uc.a.run.app';
const PROJECTS_API_BASE = `${ORBIT_GATEWAY_URL}/api/projects`;

// =============================================================================
// TYPES
// =============================================================================

/**
 * Connected app association - links a Trust-Drip provider to a project
 */
export interface ProjectApp {
  provider: string;       // Trust-Drip provider ID (e.g., 'gmail', 'google_calendar', 'slack')
  name: string;           // Display name
  icon: string;           // Emoji or icon
  connected_at: string;   // ISO timestamp
  permissions?: string[]; // Optional scoped permissions
}

export interface Project {
  id: string;
  user_id: string;
  name: string;
  icon: string;
  color: string;
  description?: string;
  instructions?: string;
  goal?: string;
  files?: string[];
  links?: string[];
  connected_apps?: ProjectApp[];  // Trust-Drip apps connected to this project
  tasks_count: number;
  created_at: string;
  updated_at: string;
  metadata?: Record<string, unknown>;
}

export interface CreateProjectRequest {
  name: string;
  icon?: string;
  color?: string;
  description?: string;
  instructions?: string;
  goal?: string;
  files?: string[];
  links?: string[];
  metadata?: Record<string, unknown>;
}

export interface UpdateProjectRequest {
  name?: string;
  icon?: string;
  color?: string;
  description?: string;
  instructions?: string;
  goal?: string;
  files?: string[];
  links?: string[];
  connected_apps?: ProjectApp[];
  tasks_count?: number;
  metadata?: Record<string, unknown>;
}

// =============================================================================
// API IMPLEMENTATION (Via Gateway)
// =============================================================================

/**
 * Get authenticated headers with Firebase token
 */
async function getHeaders(): Promise<HeadersInit> {
  const token = await getIdToken();
  const headers: HeadersInit = {
    'Content-Type': 'application/json'
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return headers;
}

/**
 * Make a POST request to the Projects API via orbit-gateway proxy
 */
async function postProjectsApi<T, R extends object = object>(endpoint: string, body: R): Promise<T> {
  const headers = await getHeaders();
  const cleanEndpoint = endpoint.replace(/^\/projects/, '');

  const response = await fetch(`${PROJECTS_API_BASE}${cleanEndpoint}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body)
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Projects API error: ${response.status} - ${errorText}`);
  }

  return response.json();
}

/**
 * Get all projects for a user
 */
export async function getUserProjects(userId: string): Promise<Project[]> {
  try {
    const data = await postProjectsApi<{ status: string, projects: Project[] }>('/list', { user_id: userId });
    return data.projects || [];
  } catch (error) {
    console.error('[ProjectsAPI] Error fetching projects:', error);
    return [];
  }
}

/**
 * Get a single project by ID
 */
export async function getProject(userId: string, projectId: string): Promise<Project | null> {
  try {
    const data = await postProjectsApi<{ status: string, project: Project }>('/get', { user_id: userId, id: projectId });
    return data.project || null;
  } catch (error) {
    console.error('[ProjectsAPI] Error fetching project:', error);
    return null;
  }
}

/**
 * Create a new project
 */
export async function createProject(userId: string, project: CreateProjectRequest): Promise<Project> {
  try {
    const data = await postProjectsApi<{ status: string, project: Project }>('/create', {
      user_id: userId,
      ...project
    });
    console.log('[ProjectsAPI] Project created:', data.project.id);
    return data.project;
  } catch (error) {
    console.error('[ProjectsAPI] Error creating project:', error);
    throw error;
  }
}

/**
 * Update an existing project
 */
export async function updateProject(
  userId: string,
  projectId: string,
  updates: UpdateProjectRequest
): Promise<Project | null> {
  try {
    const data = await postProjectsApi<{ status: string, project: Project }>('/update', {
      user_id: userId,
      id: projectId,
      ...updates
    });
    return data.project;
  } catch (error) {
    console.error('[ProjectsAPI] Error updating project:', error);
    throw error;
  }
}

/**
 * Delete a project
 */
export async function deleteProject(userId: string, projectId: string): Promise<void> {
  try {
    await postProjectsApi('/delete', { user_id: userId, id: projectId });
    console.log('[ProjectsAPI] Project deleted:', projectId);
  } catch (error) {
    console.error('[ProjectsAPI] Error deleting project:', error);
    throw error;
  }
}

/**
 * Increment tasks count for a project
 */
export async function incrementTasksCount(userId: string, projectId: string): Promise<void> {
  try {
    const project = await getProject(userId, projectId);
    if (project) {
      await updateProject(userId, projectId, {
        tasks_count: (project.tasks_count || 0) + 1
      });
    }
  } catch (error) {
    console.error('[ProjectsAPI] Error incrementing tasks count:', error);
  }
}

/**
 * File metadata stored with a project
 */
export interface ProjectFileMetadata {
  name: string;
  url: string;
  type: string;
  size: number;
}

/**
 * Add a file to a project.
 * Accepts either a plain file name (legacy) or full metadata with Storage URL.
 */
export async function addFileToProject(
  userId: string,
  projectId: string,
  fileOrName: string | ProjectFileMetadata
): Promise<void> {
  try {
    const project = await getProject(userId, projectId);
    if (project) {
      const fileName = typeof fileOrName === 'string' ? fileOrName : fileOrName.name;
      const files = [...(project.files || []), fileName];

      // Store full metadata in a separate field so the URL is preserved
      const existingMeta: ProjectFileMetadata[] =
        (project.metadata?.file_metadata as ProjectFileMetadata[] | undefined) || [];
      const fileMeta = typeof fileOrName === 'string'
        ? [...existingMeta]
        : [...existingMeta, fileOrName];

      await updateProject(userId, projectId, {
        files,
        metadata: { ...project.metadata, file_metadata: fileMeta },
      });
    }
  } catch (error) {
    console.error('[ProjectsAPI] Error adding file to project:', error);
  }
}

/**
 * Add a link to a project
 */
export async function addLinkToProject(
  userId: string,
  projectId: string,
  link: string
): Promise<void> {
  try {
    const project = await getProject(userId, projectId);
    if (project) {
      const links = [...(project.links || []), link];
      await updateProject(userId, projectId, { links });
    }
  } catch (error) {
    console.error('[ProjectsAPI] Error adding link to project:', error);
  }
}

// =============================================================================
// APP CONNECTIONS (Trust-Drip Integration)
// =============================================================================

/**
 * Available Trust-Drip apps that can be connected to projects
 * This is a curated list of commonly-used providers
 */
export const AVAILABLE_APPS: Omit<ProjectApp, 'connected_at'>[] = [
  { provider: 'gmail', name: 'Gmail', icon: '📧' },
  { provider: 'google_calendar', name: 'Google Calendar', icon: '📅' },
  { provider: 'google_drive', name: 'Google Drive', icon: '📁' },
  { provider: 'google_docs', name: 'Google Docs', icon: '📄' },
  { provider: 'google_sheets', name: 'Google Sheets', icon: '📊' },
  { provider: 'slack', name: 'Slack', icon: '💬' },
  { provider: 'notion', name: 'Notion', icon: '📝' },
  { provider: 'trello', name: 'Trello', icon: '📋' },
  { provider: 'asana', name: 'Asana', icon: '✅' },
  { provider: 'github', name: 'GitHub', icon: '🐙' },
  { provider: 'linear', name: 'Linear', icon: '🔷' },
  { provider: 'figma', name: 'Figma', icon: '🎨' },
  { provider: 'dropbox', name: 'Dropbox', icon: '📦' },
  { provider: 'twitter', name: 'Twitter/X', icon: '🐦' },
  { provider: 'linkedin', name: 'LinkedIn', icon: '💼' },
  { provider: 'hubspot', name: 'HubSpot', icon: '🧲' },
  { provider: 'salesforce', name: 'Salesforce', icon: '☁️' },
  { provider: 'stripe', name: 'Stripe', icon: '💳' },
  { provider: 'shopify', name: 'Shopify', icon: '🛒' },
  { provider: 'quickbooks', name: 'QuickBooks', icon: '📒' },
];

// =============================================================================
// PROJECT TEMPLATES
// =============================================================================

/**
 * Project template category
 */
export type TemplateCategory = 'work' | 'life' | 'creative' | 'learning';

/**
 * Project template definition
 * Pre-scoped but not pre-filled - gives users a head start
 */
export interface ProjectTemplate {
  id: string;
  name: string;
  icon: string;
  color: string;
  category: TemplateCategory;
  description: string;
  suggestedGoal: string;
  suggestedInstructions: string;
  suggestedApps: string[];  // Provider IDs
  tags: string[];
}

/**
 * Curated project templates - balanced between life and work
 */
export const PROJECT_TEMPLATES: ProjectTemplate[] = [
  // ============= WORK TEMPLATES =============
  {
    id: 'marketing-campaign',
    name: 'Marketing Campaign',
    icon: '📣',
    color: 'from-orange-400 to-red-400',
    category: 'work',
    description: 'Plan and execute a marketing campaign across channels',
    suggestedGoal: 'Launch a successful marketing campaign that drives awareness and conversions',
    suggestedInstructions: 'Help me plan content, track metrics, coordinate across channels, and optimize based on performance. Be data-driven but creative.',
    suggestedApps: ['gmail', 'google_sheets', 'twitter', 'linkedin', 'hubspot'],
    tags: ['marketing', 'campaign', 'social media', 'analytics']
  },
  {
    id: 'product-launch',
    name: 'Product Launch',
    icon: '🚀',
    color: 'from-purple-400 to-pink-400',
    category: 'work',
    description: 'Coordinate a product or feature launch',
    suggestedGoal: 'Successfully launch the product on time with maximum impact',
    suggestedInstructions: 'Track all launch tasks, coordinate between teams, manage the timeline, and help prepare launch materials. Flag any blockers immediately.',
    suggestedApps: ['slack', 'asana', 'google_docs', 'figma', 'gmail'],
    tags: ['product', 'launch', 'coordination', 'timeline']
  },
  {
    id: 'client-project',
    name: 'Client Project',
    icon: '🤝',
    color: 'from-blue-400 to-cyan-400',
    category: 'work',
    description: 'Manage a client engagement or project',
    suggestedGoal: 'Deliver excellent results for the client while maintaining clear communication',
    suggestedInstructions: 'Help me track deliverables, prepare client updates, manage scope, and maintain professional communication. Always prioritize client satisfaction.',
    suggestedApps: ['gmail', 'google_calendar', 'google_drive', 'slack', 'notion'],
    tags: ['client', 'project management', 'deliverables']
  },
  {
    id: 'sales-pipeline',
    name: 'Sales Pipeline',
    icon: '💰',
    color: 'from-green-400 to-emerald-400',
    category: 'work',
    description: 'Track and nurture sales opportunities',
    suggestedGoal: 'Convert leads into customers and grow revenue',
    suggestedInstructions: 'Help me track deals, prepare proposals, follow up with prospects, and analyze win/loss patterns. Be proactive about next steps.',
    suggestedApps: ['gmail', 'google_calendar', 'hubspot', 'salesforce', 'linkedin'],
    tags: ['sales', 'pipeline', 'leads', 'revenue']
  },
  {
    id: 'content-calendar',
    name: 'Content Calendar',
    icon: '📅',
    color: 'from-indigo-400 to-purple-400',
    category: 'work',
    description: 'Plan and schedule content across platforms',
    suggestedGoal: 'Maintain a consistent content publishing schedule',
    suggestedInstructions: 'Help me brainstorm content ideas, create a publishing schedule, track what\'s performing, and repurpose successful content.',
    suggestedApps: ['google_calendar', 'notion', 'twitter', 'linkedin', 'google_docs'],
    tags: ['content', 'scheduling', 'social media', 'publishing']
  },

  // ============= LIFE TEMPLATES =============
  {
    id: 'fitness-journey',
    name: 'Fitness Journey',
    icon: '💪',
    color: 'from-red-400 to-orange-400',
    category: 'life',
    description: 'Track workouts, nutrition, and fitness goals with conversational intelligence — log exercises, plan meals, calculate macros, and visualize progress all in one place',
    suggestedGoal: 'Build a sustainable fitness routine, hit my nutrition targets, and track measurable progress toward my health goals',
    suggestedInstructions: 'You are my fitness and nutrition coach. Help me with:\n\n**Workouts:** Log exercises conversationally (e.g. "I did 3x10 bench at 185lbs"), track sets/reps/weight, suggest progressive overload, and build weekly training splits.\n\n**Nutrition:** Track meals and snacks as I mention them, calculate calories and macros (protein/carbs/fat), suggest meal plans based on my goals (bulk/cut/maintain), and flag if I\'m under-eating protein.\n\n**Goals:** Help me set SMART fitness goals, break them into weekly milestones, and adjust based on progress.\n\n**Progress:** Generate visual progress reports as Dynamic Views — weight trends, lift PRs, macro adherence charts, and weekly summaries.\n\nBe encouraging but push me when I slack. Use my connected apps (fitness trackers, food logs, spreadsheets) to pull real data when available. If I miss a workout or meal log, check in.',
    suggestedApps: ['google_calendar', 'google_sheets', 'google_drive'],
    tags: ['fitness', 'health', 'workout', 'nutrition', 'macros', 'meal-plan', 'progress']
  },
  {
    id: 'home-renovation',
    name: 'Home Renovation',
    icon: '🏠',
    color: 'from-amber-400 to-yellow-400',
    category: 'life',
    description: 'Plan and manage home improvement projects',
    suggestedGoal: 'Complete the renovation on time and within budget',
    suggestedInstructions: 'Help me track contractors, manage the budget, research materials, and keep the timeline on track. Warn me about potential issues.',
    suggestedApps: ['google_sheets', 'google_drive', 'gmail'],
    tags: ['home', 'renovation', 'budget', 'contractors']
  },
  {
    id: 'vacation-planning',
    name: 'Vacation Planning',
    icon: '✈️',
    color: 'from-sky-400 to-blue-400',
    category: 'life',
    description: 'Plan an amazing trip from start to finish',
    suggestedGoal: 'Have an unforgettable, well-organized vacation',
    suggestedInstructions: 'Help me research destinations, book accommodations, create itineraries, and handle all the logistics. Make it stress-free!',
    suggestedApps: ['google_calendar', 'gmail', 'google_docs'],
    tags: ['travel', 'vacation', 'planning', 'itinerary']
  },
  {
    id: 'wedding-planning',
    name: 'Wedding Planning',
    icon: '💒',
    color: 'from-pink-400 to-rose-400',
    category: 'life',
    description: 'Organize every detail of the big day',
    suggestedGoal: 'Create a beautiful, memorable wedding day',
    suggestedInstructions: 'Help me manage vendors, track the budget, coordinate with the wedding party, and handle all the details. Keep me calm and organized!',
    suggestedApps: ['google_sheets', 'google_calendar', 'gmail', 'google_drive'],
    tags: ['wedding', 'event', 'planning', 'vendors']
  },
  {
    id: 'family-finances',
    name: 'Family Finances',
    icon: '💵',
    color: 'from-emerald-400 to-teal-400',
    category: 'life',
    description: 'Manage household budget and financial goals',
    suggestedGoal: 'Achieve financial stability and reach savings goals',
    suggestedInstructions: 'Help me track expenses, plan the budget, identify savings opportunities, and stay on track with financial goals. Be honest about spending patterns.',
    suggestedApps: ['google_sheets', 'quickbooks'],
    tags: ['finance', 'budget', 'savings', 'family']
  },
  {
    id: 'new-parent',
    name: 'New Parent',
    icon: '👶',
    color: 'from-violet-400 to-purple-400',
    category: 'life',
    description: 'Navigate the journey of new parenthood',
    suggestedGoal: 'Be the best parent I can be while taking care of myself',
    suggestedInstructions: 'Help me track baby milestones, manage appointments, research parenting topics, and remember self-care. Be supportive and non-judgmental.',
    suggestedApps: ['google_calendar', 'google_docs'],
    tags: ['parenting', 'baby', 'family', 'milestones']
  },

  // ============= CREATIVE TEMPLATES =============
  {
    id: 'creative-project',
    name: 'Creative Project',
    icon: '🎨',
    color: 'from-fuchsia-400 to-pink-400',
    category: 'creative',
    description: 'Bring a creative vision to life',
    suggestedGoal: 'Complete my creative project and share it with the world',
    suggestedInstructions: 'Help me brainstorm ideas, overcome creative blocks, manage the process, and get feedback. Encourage experimentation but help me finish.',
    suggestedApps: ['figma', 'notion', 'google_drive'],
    tags: ['creative', 'art', 'design', 'project']
  },
  {
    id: 'writing-project',
    name: 'Writing Project',
    icon: '✍️',
    color: 'from-slate-400 to-gray-400',
    category: 'creative',
    description: 'Write a book, blog, or long-form content',
    suggestedGoal: 'Complete and publish my writing project',
    suggestedInstructions: 'Help me outline, overcome writer\'s block, edit drafts, and stay on schedule. Be a supportive writing partner who pushes me to finish.',
    suggestedApps: ['google_docs', 'notion'],
    tags: ['writing', 'book', 'content', 'publishing']
  },

  // ============= LEARNING TEMPLATES =============
  {
    id: 'learn-new-skill',
    name: 'Learn New Skill',
    icon: '📚',
    color: 'from-cyan-400 to-blue-400',
    category: 'learning',
    description: 'Master a new skill or subject',
    suggestedGoal: 'Achieve proficiency in my chosen skill',
    suggestedInstructions: 'Help me create a learning plan, find resources, practice consistently, and track progress. Quiz me and keep me accountable.',
    suggestedApps: ['google_calendar', 'notion', 'google_docs'],
    tags: ['learning', 'skill', 'education', 'growth']
  },
  {
    id: 'career-development',
    name: 'Career Development',
    icon: '📈',
    color: 'from-blue-400 to-indigo-400',
    category: 'learning',
    description: 'Grow professionally and advance your career',
    suggestedGoal: 'Reach the next level in my career',
    suggestedInstructions: 'Help me identify growth areas, prepare for opportunities, build my network, and track achievements. Be my career coach.',
    suggestedApps: ['linkedin', 'google_calendar', 'gmail', 'notion'],
    tags: ['career', 'growth', 'professional', 'networking']
  },
];

/**
 * Get templates by category
 */
export function getTemplatesByCategory(category: TemplateCategory): ProjectTemplate[] {
  return PROJECT_TEMPLATES.filter(t => t.category === category);
}

/**
 * Get all template categories with counts
 */
export function getTemplateCategories(): { category: TemplateCategory; count: number; label: string }[] {
  return [
    { category: 'work', count: PROJECT_TEMPLATES.filter(t => t.category === 'work').length, label: 'Work' },
    { category: 'life', count: PROJECT_TEMPLATES.filter(t => t.category === 'life').length, label: 'Life' },
    { category: 'creative', count: PROJECT_TEMPLATES.filter(t => t.category === 'creative').length, label: 'Creative' },
    { category: 'learning', count: PROJECT_TEMPLATES.filter(t => t.category === 'learning').length, label: 'Learning' },
  ];
}

/**
 * Create a project from a template
 */
export async function createProjectFromTemplate(
  userId: string,
  template: ProjectTemplate,
  customizations?: Partial<CreateProjectRequest>
): Promise<Project> {
  const projectData: CreateProjectRequest = {
    name: customizations?.name || template.name,
    icon: customizations?.icon || template.icon,
    color: customizations?.color || template.color,
    description: customizations?.description || template.description,
    instructions: customizations?.instructions || template.suggestedInstructions,
    goal: customizations?.goal || template.suggestedGoal,
    metadata: {
      template_id: template.id,
      template_category: template.category,
      suggested_apps: template.suggestedApps,
      ...customizations?.metadata
    }
  };

  return createProject(userId, projectData);
}

/**
 * Connect an app to a project
 */
export async function connectAppToProject(
  userId: string,
  projectId: string,
  app: Omit<ProjectApp, 'connected_at'>
): Promise<void> {
  try {
    const project = await getProject(userId, projectId);
    if (!project) throw new Error('Project not found');

    const existingApps = project.connected_apps || [];

    // Check if already connected
    if (existingApps.some(a => a.provider === app.provider)) {
      console.log('[ProjectsAPI] App already connected:', app.provider);
      return;
    }

    const newApp: ProjectApp = {
      ...app,
      connected_at: new Date().toISOString()
    };

    await updateProject(userId, projectId, {
      connected_apps: [...existingApps, newApp]
    });

    console.log('[ProjectsAPI] App connected to project:', app.provider);
  } catch (error) {
    console.error('[ProjectsAPI] Error connecting app:', error);
    throw error;
  }
}

/**
 * Disconnect an app from a project
 */
export async function disconnectAppFromProject(
  userId: string,
  projectId: string,
  provider: string
): Promise<void> {
  try {
    const project = await getProject(userId, projectId);
    if (!project) throw new Error('Project not found');

    const updatedApps = (project.connected_apps || []).filter(
      a => a.provider !== provider
    );

    await updateProject(userId, projectId, {
      connected_apps: updatedApps
    });

    console.log('[ProjectsAPI] App disconnected from project:', provider);
  } catch (error) {
    console.error('[ProjectsAPI] Error disconnecting app:', error);
    throw error;
  }
}

/**
 * Get available apps that are not yet connected to this project
 */
export async function getAvailableAppsForProject(
  userId: string,
  projectId: string
): Promise<Omit<ProjectApp, 'connected_at'>[]> {
  try {
    const project = await getProject(userId, projectId);
    if (!project) return AVAILABLE_APPS;

    const connectedProviders = new Set(
      (project.connected_apps || []).map(a => a.provider)
    );

    return AVAILABLE_APPS.filter(app => !connectedProviders.has(app.provider));
  } catch (error) {
    console.error('[ProjectsAPI] Error getting available apps:', error);
    return AVAILABLE_APPS;
  }
}
